// client/src/services/customerAPI.js
// UPDATED: Added deleteVisit method

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach JWT token to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Global response handler
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/auth/login';
    }
    return Promise.reject(error);
  }
);

export const customerAPI = {
  // Get all customers
  getAll: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.source) params.append('source', filters.source);
      
      const response = await api.get(`/customers?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('API: Get customers error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Get single customer
  getById: async (id) => {
    try {
      const response = await api.get(`/customers/${id}`);
      return response.data;
    } catch (error) {
      console.error('API: Get customer error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Create customer (manual entry)
  create: async (customerData) => {
    try {
      const response = await api.post('/customers', customerData);
      return response.data;
    } catch (error) {
      console.error('API: Create customer error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // AI Extract customer from text
  extract: async (text) => {
    try {
      console.log('🤖 Frontend: Requesting customer extraction...');
      const response = await api.post('/customers/extract', { text });
      console.log('✅ Frontend: Customer extraction response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Frontend: Customer extraction error:', error.response?.data || error);
      return {
        success: false,
        error: 'Failed to extract customer information. Please try manual entry.'
      };
    }
  },

  // Update customer
  update: async (id, customerData) => {
    try {
      const response = await api.patch(`/customers/${id}`, customerData);
      return response.data;
    } catch (error) {
      console.error('API: Update customer error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Delete customer
  delete: async (id) => {
    try {
      const response = await api.delete(`/customers/${id}`);
      return response.data;
    } catch (error) {
      console.error('API: Delete customer error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Add note to customer
  addNote: async (id, note) => {
    try {
      const response = await api.post(`/customers/${id}/notes`, { note });
      return response.data;
    } catch (error) {
      console.error('API: Add note error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Get customer activities
  getActivities: async (id) => {
    try {
      const response = await api.get(`/customers/${id}/activities`);
      return response.data;
    } catch (error) {
      console.error('API: Get activities error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Add reminder
  addReminder: async (id, reminderData) => {
    try {
      const response = await api.post(`/customers/${id}/reminders`, reminderData);
      return response.data;
    } catch (error) {
      console.error('API: Add reminder error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Export customers to CSV
  exportCSV: async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters);
      const response = await api.get(`/customers/export?${params.toString()}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('API: Export customers error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // ============================================
  // VISIT TRACKING METHODS
  // ============================================

  // Create visit for customer
  createVisit: async (customerId, visitData) => {
    try {
      const response = await api.post(`/customers/${customerId}/visits`, visitData);
      return response.data;
    } catch (error) {
      console.error('API: Create visit error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Get visit history for customer
  getVisitHistory: async (customerId, limit = 50) => {
    try {
      const response = await api.get(`/customers/${customerId}/visits?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('API: Get visit history error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // NEW: Delete a visit
  deleteVisit: async (customerId, visitId) => {
    try {
      const response = await api.delete(`/customers/${customerId}/visits/${visitId}`);
      return response.data;
    } catch (error) {
      console.error('API: Delete visit error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Get visit statistics
  getVisitStats: async () => {
    try {
      const response = await api.get('/customers/stats/visits');
      return response.data;
    } catch (error) {
      console.error('API: Get visit stats error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // ============================================
  // SEGMENTATION METHODS
  // ============================================

  // Get customers by segment
  getSegment: async (segmentType) => {
    try {
      const response = await api.get(`/customers/segments/list?segment=${segmentType}`);
      return response.data;
    } catch (error) {
      console.error('API: Get segment error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Get segment statistics
  getSegmentStats: async () => {
    try {
      const response = await api.get('/customers/segments/stats');
      return response.data;
    } catch (error) {
      console.error('API: Get segment stats error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // ============================================
  // ANALYTICS & RETENTION METHODS
  // ============================================

  // Get retention metrics
  getRetentionMetrics: async () => {
    try {
      const response = await api.get('/customers/analytics/retention');
      return response.data;
    } catch (error) {
      console.error('API: Get retention metrics error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Get dormant customers
  getDormantCustomers: async (threshold = 40) => {
    try {
      const response = await api.get(`/customers/analytics/dormant?threshold=${threshold}`);
      return response.data;
    } catch (error) {
      console.error('API: Get dormant customers error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Get VIP customers
  getVIPCustomers: async () => {
    try {
      const response = await api.get('/customers/analytics/vip');
      return response.data;
    } catch (error) {
      console.error('API: Get VIP customers error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // ============================================
  // AI COMMUNICATION METHODS
  // ============================================

  // Generate AI customer summary
  generateCustomerSummary: async (customerId) => {
    try {
      const response = await api.post(`/customers/${customerId}/ai/summary`);
      return response.data;
    } catch (error) {
      console.error('API: Generate summary error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Generate AI email content
  generateEmailContent: async (customerId, templateType = 'general') => {
    try {
      const response = await api.post(`/customers/${customerId}/ai/email`, { templateType });
      return response.data;
    } catch (error) {
      console.error('API: Generate email error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  },

  // Send bulk email to segment
  sendSegmentEmail: async (segment, subject, htmlBody) => {
    try {
      const response = await api.post('/customers/communication/send-segment-email', {
        segment,
        subject,
        htmlBody
      });
      return response.data;
    } catch (error) {
      console.error('API: Send segment email error:', error.response?.data || error.message);
      throw error.response?.data || error;
    }
  }
};

export default customerAPI;